<template>
    <div>
        <header-portrate />
        <exports-print ref="exportbar" />

        <div>
            <bread-crumbs :items="breadcrumbItems"></bread-crumbs>
            <tabs-comp :tabs="tabs" />

            <v-row style="margin-top:10px;" class="p-2">
                <v-col cols="12">
                    <v-card outlined class="attendance-shell pa-3">
                        <div class="d-flex flex-wrap align-center justify-space-between mb-2">
                            <div>
                                <h6 class="mb-1 attendance-title">{{ txt('att_attendance_exception_mgmt', 'Attendance & Exception Management') }}</h6>
                                <div class="caption grey--text text--darken-1">{{ txt('att_daily_attendance_tracking', 'Daily Attendance Tracking') }}</div>
                            </div>
                            <v-text-field
                                v-model="selectedDate"
                                type="date"
                                dense
                                outlined
                                hide-details
                                class="date-input"
                                :label="txt('att_date', 'Date')"
                            />
                        </div>

                        <v-row class="mb-2">
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_employees', 'Employees') }}</div>
                                    <div class="kpi-value">{{ kpis.total }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_on_time', 'On Time') }}</div>
                                    <div class="kpi-value green--text text--darken-2">{{ kpis.onTime }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_late', 'Late') }}</div>
                                    <div class="kpi-value orange--text text--darken-2">{{ kpis.late }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_absent', 'Absent') }}</div>
                                    <div class="kpi-value red--text text--darken-2">{{ kpis.absent }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_vacation', 'Vacation') }}</div>
                                    <div class="kpi-value purple--text text--darken-2">{{ kpis.vacation }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-card outlined class="kpi-card pa-2">
                                    <div class="kpi-label">{{ txt('att_exceptions', 'Exceptions') }}</div>
                                    <div class="kpi-value">{{ kpis.exceptions }}</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-divider class="mb-3" />

                        <v-form ref="attendanceForm" lazy-validation>
                            <v-row>
                                <v-col
                                    v-for="record in attendanceRecords"
                                    :key="record.employeeId"
                                    cols="12"
                                    lg="6"
                                >
                                    <v-card outlined class="attendance-card pa-3">
                                        <div class="d-flex justify-space-between align-start mb-2">
                                            <div>
                                                <div class="employee-name">{{ record.employeeName }}</div>
                                                <div class="employee-meta">
                                                    {{ txt('att_branch', 'Branch') }}: {{ record.branchName }}
                                                    <v-tooltip bottom v-if="record.reason && String(record.reason).trim()">
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon
                                                                small
                                                                class="ml-1"
                                                                v-bind="attrs"
                                                                v-on="on"
                                                            >
                                                                mdi-note-text-outline
                                                            </v-icon>
                                                        </template>
                                                        <span>{{ record.reason }}</span>
                                                    </v-tooltip>
                                                </div>
                                            </div>
                                            <v-chip
                                                small
                                                dark
                                                :color="statusMeta(record).color"
                                            >
                                                {{ statusMeta(record).label }}
                                            </v-chip>
                                        </div>

                                        <v-row dense>
                                            <v-col cols="6" md="3">
                                                <v-text-field
                                                    v-model="record.shiftStart"
                                                    type="time"
                                                    dense
                                                    outlined
                                                    :disabled="record.notAttended"
                                                    :label="txt('att_shift_start', 'Shift Start')"
                                                    :rules="timeRules(record)"
                                                />
                                            </v-col>
                                            <v-col cols="6" md="3">
                                                <v-text-field
                                                    v-model="record.actualArrival"
                                                    type="time"
                                                    dense
                                                    outlined
                                                    :disabled="record.notAttended"
                                                    :label="txt('att_actual_arrival', 'Actual Arrival')"
                                                    :rules="timeRules(record)"
                                                />
                                            </v-col>
                                            <v-col cols="6" md="3">
                                                <v-text-field
                                                    v-model="record.shiftEnd"
                                                    type="time"
                                                    dense
                                                    outlined
                                                    :disabled="record.notAttended"
                                                    :label="txt('att_shift_end', 'Shift End')"
                                                    :rules="timeRules(record)"
                                                />
                                            </v-col>
                                            <v-col cols="6" md="3">
                                                <v-text-field
                                                    v-model="record.actualDeparture"
                                                    type="time"
                                                    dense
                                                    outlined
                                                    :disabled="record.notAttended"
                                                    :label="txt('att_actual_departure', 'Actual Departure')"
                                                    :rules="timeRules(record)"
                                                />
                                            </v-col>
                                        </v-row>

                                        <v-row dense class="align-center">
                                            <v-col cols="12" md="4">
                                                <v-switch
                                                    v-model="record.notAttended"
                                                    inset
                                                    hide-details
                                                    class="mt-0"
                                                    :label="txt('att_not_attended', 'Not Attended')"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="8" v-if="record.notAttended">
                                                <v-select
                                                    v-model="record.absenceType"
                                                    :items="absenceOptions"
                                                    item-text="text"
                                                    item-value="value"
                                                    dense
                                                    outlined
                                                    clearable
                                                    :label="txt('att_absence_type', 'Absence Type')"
                                                    :rules="absenceRules(record)"
                                                />
                                            </v-col>
                                        </v-row>

                                        <v-row dense v-if="requiresExceptionInputs(record)">
                                            <v-col cols="12" md="4" v-if="!record.notAttended">
                                                <v-select
                                                    v-model="record.justificationType"
                                                    :items="justificationOptions"
                                                    item-text="text"
                                                    item-value="value"
                                                    dense
                                                    outlined
                                                    clearable
                                                    :label="txt('att_justification_type', 'Justification Type')"
                                                    :rules="justificationRules(record)"
                                                />
                                            </v-col>
                                            <v-col cols="12" :md="record.notAttended ? 12 : 8">
                                                <v-textarea
                                                    v-model="record.reason"
                                                    dense
                                                    outlined
                                                    rows="2"
                                                    auto-grow
                                                    :label="txt('att_reason', 'Reason')"
                                                    :rules="reasonRules(record)"
                                                />
                                            </v-col>
                                        </v-row>

                                        <div
                                            v-if="record.reason && String(record.reason).trim()"
                                            class="reason-inline"
                                        >
                                            <v-icon x-small class="mr-1">mdi-note-text</v-icon>
                                            {{ record.reason }}
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-form>

                        <div class="d-flex justify-end mt-3">
                            <v-btn
                                color="primary"
                                :loading="submitting"
                                @click="submitAttendance"
                            >
                                {{ txt('att_save_attendance', 'Save Attendance') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <Footer />
    </div>
</template>

<script>
import axios from 'axios';
import breadCrumbs from '@/components/breadCrumbs.vue';
import TabsComp from '@/components/tabsComp.vue';
import HeaderPortrate from '@/components/Header-Portrate.vue';
import Footer from '@/components/Footer.vue';
import ExportsPrint from '@/components/exports-print.vue';
import { getVisibleTabsForRoute } from '@/menu/menuService'

export default {
    components: { ExportsPrint, breadCrumbs, TabsComp, HeaderPortrate, Footer },
    name: 'salary-table',
    data() {
        return {
            selectedDate: this.todayISO(),
            submitting: false,
            attendanceRecords: []
        };
    },
    created() {
        this.getEmployees();
    },
    methods: {
        getEmployees() {
            const post = new FormData();
            post.append("type" , "getEmployees");
            post.append('defBranch',localStorage.getItem('defBranch'));
            post.append('financeYear',localStorage.getItem('financeYear'));
            post.append("auth",this.$cookies.get(this.$store.state.COOKIEPhase));
            post.append('data[month]','');
            post.append('data[year]','');

            axios.post(
                this.$store.state.SAMCOTEC.r_path, post
            ).then((response) => {
                const responseData = response && response.data ? response.data : {};
                const nestedData = responseData.results && responseData.results.data ? responseData.results.data : {};
                let employees = [];

                if (Array.isArray(nestedData.results)) {
                    employees = nestedData.results;
                } else if (Array.isArray(nestedData)) {
                    employees = nestedData;
                } else if (responseData.results && Array.isArray(responseData.results.data1)) {
                    employees = responseData.results.data1;
                }

                this.attendanceRecords = employees.map((employee) => this.mapEmployeeToAttendanceRecord(employee));
                this.getCurrentDateAttendeesValues();
            }).catch(() => {
                this.attendanceRecords = [];
                if (this.$store && this.$store.commit) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        text: this.txt('att_load_employees_failed', 'Could not load employees'),
                        color: 'warning'
                    });
                }
            });
        },
        getCurrentDateAttendeesValues() {
            if (!this.selectedDate || !Array.isArray(this.attendanceRecords) || !this.attendanceRecords.length) {
                return;
            }

            this.resetAttendanceRecordsForDate();

            const post = new FormData();
            post.append('type', 'getEmployeesNattendees');
            post.append('defBranch', localStorage.getItem('defBranch'));
            post.append('financeYear', localStorage.getItem('financeYear'));
            post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase));
            post.append('data[attendance_date]', this.selectedDate);
            post.append('data[date]', this.selectedDate);

            axios.post(this.$store.state.SAMCOTEC.r_path, post)
                .then((response) => {
                    const responseData = response && response.data ? response.data : {};
                    const dataNode = responseData.results && responseData.results.data ? responseData.results.data : {};

                    let attendees = [];
                    if (Array.isArray(dataNode.results)) {
                        attendees = dataNode.results;
                    } else if (Array.isArray(dataNode)) {
                        attendees = dataNode;
                    } else if (responseData.results && Array.isArray(responseData.results.data1)) {
                        attendees = responseData.results.data1;
                    } else if (Array.isArray(responseData.results)) {
                        attendees = responseData.results;
                    }

                    this.applyAttendeesValues(attendees);
                })
                .catch(() => {
                    console.log('Attendance values for selected date are not available yet:', this.selectedDate);
                });
        },
        resetAttendanceRecordsForDate() {
            this.attendanceRecords = this.attendanceRecords.map((record) => ({
                ...record,
                shiftStart: '08:00',
                actualArrival: '08:00',
                shiftEnd: '17:00',
                actualDeparture: '17:00',
                notAttended: false,
                late: false,
                earlyDismissal: false,
                absenceType: null,
                justificationType: null,
                reason: ''
            }));
        },
        applyAttendeesValues(attendees) {
            if (!Array.isArray(attendees) || !attendees.length) {
                return;
            }

            const byEmployeeId = {};
            attendees.forEach((item) => {
                if (!item) return;
                const employeeId = String(
                    item.employee_id ||
                    item.employeeId ||
                    item.empid ||
                    item.id ||
                    ''
                );
                if (!employeeId) return;
                byEmployeeId[employeeId] = item;
            });

            this.attendanceRecords = this.attendanceRecords.map((record) => {
                const found = byEmployeeId[String(record.employeeId)];
                if (!found) return record;

                const notAttended = this.toBoolean(found.not_attended, record.notAttended);

                return {
                    ...record,
                    shiftStart: found.shift_start || found.shiftStart || record.shiftStart,
                    actualArrival: found.actual_arrival || found.actualArrival || record.actualArrival,
                    shiftEnd: found.shift_end || found.shiftEnd || record.shiftEnd,
                    actualDeparture: found.actual_departure || found.actualDeparture || record.actualDeparture,
                    notAttended,
                    late: this.toBoolean(found.late, false),
                    earlyDismissal: this.toBoolean(found.early_dismissal || found.earlyDismissal, false),
                    absenceType: found.absence_type || found.absenceType || null,
                    justificationType: found.justification_type || found.justificationType || null,
                    reason: found.reason || ''
                };
            });
        },
        toBoolean(value, fallback = false) {
            if (value === true || value === false) return value;
            if (value === 1 || value === '1') return true;
            if (value === 0 || value === '0') return false;
            return fallback;
        },
        mapEmployeeToAttendanceRecord(employee) {
            const branchRaw = employee && (employee.branchid || employee.branch_id || employee.branch);
            const branchName = branchRaw ? String(branchRaw) : this.txt('att_unknown_branch', 'Unknown Branch');

            return {
                employeeId: employee && employee.id ? String(employee.id) : '',
                employeeName: (employee && employee.employee_name) ? employee.employee_name : this.txt('att_unknown_employee', 'Unknown Employee'),
                branchName,
                shiftStart: '08:00',
                actualArrival: '08:00',
                shiftEnd: '17:00',
                actualDeparture: '17:00',
                notAttended: false,
                absenceType: null,
                justificationType: null,
                reason: ''
            };
        },
        txt(key, fallback) {
            return (this.lang && this.lang[key]) ? this.lang[key] : fallback;
        },
        todayISO() {
            const now = new Date();
            const y = now.getFullYear();
            const m = `${now.getMonth() + 1}`.padStart(2, '0');
            const d = `${now.getDate()}`.padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        toMinutes(timeValue) {
            if (!timeValue || String(timeValue).indexOf(':') === -1) return null;
            const parts = String(timeValue).split(':').map(Number);
            if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
            return parts[0] * 60 + parts[1];
        },
        isLate(record) {
            if (record.notAttended) return false;
            const shift = this.toMinutes(record.shiftStart);
            const arrival = this.toMinutes(record.actualArrival);
            if (shift === null || arrival === null) return false;
            return arrival > shift;
        },
        isEarlyDismissal(record) {
            if (record.notAttended) return false;
            const shiftEnd = this.toMinutes(record.shiftEnd);
            const departure = this.toMinutes(record.actualDeparture);
            if (shiftEnd === null || departure === null) return false;
            return departure < shiftEnd;
        },
        requiresExceptionInputs(record) {
            if (record.notAttended) return true;
            return this.isLate(record) || this.isEarlyDismissal(record);
        },
        requiresReason(record) {
            if (record.notAttended && record.absenceType === 'absent') return true;
            return this.isLate(record) || this.isEarlyDismissal(record);
        },
        timeRules(record) {
            return [
                (value) => {
                    if (record.notAttended) return true;
                    return !!value || this.txt('att_required_field', 'Required field');
                }
            ];
        },
        absenceRules(record) {
            return [
                (value) => {
                    if (!record.notAttended) return true;
                    return !!value || this.txt('att_absence_type_required', 'Absence type is required');
                }
            ];
        },
        justificationRules(record) {
            return [
                (value) => {
                    if (!this.requiresExceptionInputs(record)) return true;
                    if (record.notAttended) return true;
                    return !!value || this.txt('att_justification_type_required', 'Justification type is required');
                }
            ];
        },
        reasonRules(record) {
            return [
                (value) => {
                    if (!this.requiresReason(record)) return true;
                    return !!String(value || '').trim() || this.txt('att_reason_required', 'Reason is required');
                }
            ];
        },
        statusMeta(record) {
            if (record.notAttended) {
                if (record.absenceType === 'vacation') {
                    return { color: 'purple', label: this.txt('att_on_vacation', 'On Vacation') };
                }
                if (record.absenceType === 'absent') {
                    return { color: 'red', label: this.txt('att_absent', 'Absent') };
                }
                if (record.absenceType === 'permission') {
                    return { color: 'orange', label: this.txt('att_permission', 'Permission') };
                }
                return { color: 'red', label: this.txt('att_not_attended', 'Not Attended') };
            }

            const late = this.isLate(record);
            const early = this.isEarlyDismissal(record);
            if (!late && !early) {
                return { color: 'green', label: this.txt('att_present_on_time', 'Present & On Time') };
            }

            const justified = this.justificationBoolean(record);
            if (justified === true) {
                return { color: 'orange', label: this.txt('att_late_justified', 'Late (Justified)') };
            }

            return { color: 'red', label: this.txt('att_late_early_unjustified', 'Late / Early (Unjustified)') };
        },
        justificationBoolean(record) {
            if (record.notAttended) {
                const selectedAbsence = this.absenceOptions.find((item) => item.value === record.absenceType);
                return selectedAbsence ? selectedAbsence.justified : false;
            }

            const selectedJustification = this.justificationOptions.find((item) => item.value === record.justificationType);
            if (!selectedJustification) return false;
            return selectedJustification.justified;
        },
        dashboardApiPath() {
            const base =
                this.$store &&
                this.$store.state &&
                this.$store.state.SAMCOTEC &&
                this.$store.state.SAMCOTEC.r_path
                    ? this.$store.state.SAMCOTEC.r_path
                    : '';
            if (base && base.indexOf('index.php') !== -1) {
                return base.replace('index.php', 'new-dashboard.php');
            }
            if (base && base.endsWith('/')) {
                return `${base}new-dashboard.php`;
            }
            return 'api/new-dashboard.php';
        },
        submitAttendance() {
            if (!this.$refs.attendanceForm || !this.$refs.attendanceForm.validate()) {
                return;
            }

            const payload = {
                module: 'attendance',
                attendanceDate: this.selectedDate,
                records: this.attendanceRecords.map((record) => {
                    const late = this.isLate(record);
                    const earlyDismissal = this.isEarlyDismissal(record);
                    const justified = this.justificationBoolean(record);

                    return {
                        employeeId: record.employeeId,
                        shiftStart: record.shiftStart,
                        actualArrival: record.actualArrival,
                        shiftEnd: record.shiftEnd,
                        actualDeparture: record.actualDeparture,
                        notAttended: !!record.notAttended,
                        late,
                        earlyDismissal,
                        absenceType: record.absenceType,
                        justificationType: record.justificationType,
                        reason: record.reason,
                        justified: justified === true
                    };
                })
            };

            this.submitting = true;
            this.saveCurrentDateAttendees(payload)
                .then(() => {
                    if (this.$store && this.$store.commit) {
                        this.$store.commit('setSnackbar', {
                            show: true,
                            text: this.txt('att_submit_success', 'Attendance submitted successfully'),
                            color: 'success'
                        });
                    }
                })
                .catch(() => {
                    console.log('Attendance payload (backend pending):', payload);
                    if (this.$store && this.$store.commit) {
                        this.$store.commit('setSnackbar', {
                            show: true,
                            text: this.txt('att_backend_pending', 'Backend endpoint not ready yet, payload logged'),
                            color: 'warning'
                        });
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        saveCurrentDateAttendees(payload) {
            const post = new FormData();

            post.append('type', 'addEmployeesNattendees');
            post.append('defBranch', localStorage.getItem('defBranch'));
            post.append('financeYear', localStorage.getItem('financeYear'));
            post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase));
            post.append('data[attendance_date]', this.selectedDate);
            post.append('data[attendance_payload]', JSON.stringify(payload));

            return axios.post(this.$store.state.SAMCOTEC.r_path, post);
        }
    },
    computed: {
        breadcrumbItems() {
            return [
                { text: this.txt('att_hr', 'HR'), disabled: false, href: '' },
                { text: this.txt('att_attendance', 'Attendance'), disabled: true, href: '' }
            ];
        },
        justificationOptions() {
            return [
                { text: this.txt('att_justified_delay', 'Justified Delay'), value: 'justified_delay', justified: true },
                { text: this.txt('att_justified_departure', 'Justified Departure'), value: 'justified_departure', justified: true },
                { text: this.txt('att_unjustified', 'Unjustified'), value: 'unjustified', justified: false }
            ];
        },
        absenceOptions() {
            return [
                { text: this.txt('att_permission_approved_leave', 'Permission (Approved Leave)'), value: 'permission', justified: true },
                { text: this.txt('att_vacation', 'Vacation'), value: 'vacation', justified: true },
                { text: this.txt('att_not_justified_absent', 'Not Justified (Absent)'), value: 'absent', justified: false }
            ];
        },
        lang: function(){
            if(this.$cookies.get(this.$store.state.LangCooki) != null){
                if(this.$cookies.get(this.$store.state.LangCooki)  == 'en'){
                    return this.$store.state.lang.en;
                }else if(this.$cookies.get(this.$store.state.LangCooki)  == 'ur'){
                    return this.$store.state.lang.ur;
                }else{
                    return this.$store.state.lang.ar;
                }
            }
            else if(this.$store.state.deflang == 'en'){
                return this.$store.state.lang.en;
            }
            else if(this.$store.state.deflang == 'ur'){
                return this.$store.state.lang.ur;
            }else{
                return this.$store.state.lang.ar;
            }
        },
        tabs: function() {

            return getVisibleTabsForRoute(this.$store.getters['state'], this.lang, this.$route.path);

        },
        kpis() {
            const total = this.attendanceRecords.length;
            let onTime = 0;
            let late = 0;
            let absent = 0;
            let vacation = 0;
            let exceptions = 0;

            this.attendanceRecords.forEach((record) => {
                const isLate = this.isLate(record);
                const isEarly = this.isEarlyDismissal(record);
                if (record.notAttended) {
                    exceptions += 1;
                    if (record.absenceType === 'vacation') vacation += 1;
                    if (record.absenceType === 'absent') absent += 1;
                    return;
                }

                if (!isLate && !isEarly) {
                    onTime += 1;
                } else {
                    late += 1;
                    exceptions += 1;
                }
            });

            return { total, onTime, late, absent, vacation, exceptions };
        }
    },
    watch: {
        selectedDate(newValue, oldValue) {
            if (!newValue || newValue === oldValue) return;
            this.getCurrentDateAttendeesValues();
        }
    }
};
</script>

<style scoped>
.attendance-shell {
    border-color: #e3e8ef !important;
    background: #ffffff;
}

.attendance-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1f2937;
}

.date-input {
    max-width: 220px;
}

.kpi-card {
    height: 100%;
    border-color: #e7edf4 !important;
    border-radius: 10px;
}

.kpi-label {
    font-size: 0.74rem;
    color: #64748b;
    font-weight: 600;
}

.kpi-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
}

.attendance-card {
    border-color: #e7edf4 !important;
    border-radius: 10px;
    background: #fcfdff;
}

.employee-name {
    font-size: 0.92rem;
    font-weight: 700;
    color: #1f2937;
}

.employee-meta {
    font-size: 0.76rem;
    color: #64748b;
    display: inline-flex;
    align-items: center;
}

.reason-inline {
    font-size: 0.78rem;
    color: #334155;
    background: #f8fafc;
    border: 1px dashed #d7e1ea;
    border-radius: 8px;
    padding: 6px 8px;
    margin-top: 4px;
    display: flex;
    align-items: center;
}
</style>
